import type { ApplicationSettingThemeColor } from '@lighthouse/core'
import { useUncontrolled } from '@lighthouse/tools'
import chroma from 'chroma-js'
import React, { useCallback, useEffect, useState } from 'react'
import { useLatest } from 'react-use'

import { useFillPickerContext } from '../Provider'
import type { HsvaColor } from '../type'
import { isHexColor, isHsvaColor } from '../utils'
import { ColorInputContainer } from './ColorInputContainer'
import { ColorLibrary } from './ColorLibrary'
import { ColorPickerContainer } from './ColorPickerContainer'
import { RecentColors } from './RecentColors'
import { SaturationValuePanel } from './SaturationValuePanel'

function hexToHsva(value: string): HsvaColor {
    const color = chroma(value)
    const [h, s, v] = color.hsv()
    const alpha = color.alpha()
    return { h: Number.isNaN(h) ? 0 : h, s, v, a: alpha }
}

function inputToHsva(value: string, palettes: ApplicationSettingThemeColor[]): HsvaColor {
    const hex =
        /* value === APP_MAIN_COLOR_VARIABLE ? window.getComputedStyle(document.body).getPropertyValue(APP_MAIN_COLOR_VARIABLE) :  */ value

    if (isHexColor(hex)) {
        return hexToHsva(hex)
    }

    const libColor = palettes.find(item => item.id === value)
    if (!libColor) {
        return { h: 0, s: 0, v: 0, a: 1 }
    }

    return hexToHsva(libColor.value)
}

function hsvaToHex(value: HsvaColor): string {
    return chroma(value.h, value.s, value.v, value.a, 'hsv').hex()
}

function inputToValue(input: HsvaColor /* | typeof APP_MAIN_COLOR_VARIABLE */ | ApplicationSettingThemeColor) {
    // if (input === APP_MAIN_COLOR_VARIABLE) {
    //     return input
    // }

    if (isHsvaColor(input)) {
        return hsvaToHex(input)
    }

    return input.id
}

interface FillPickerCoreProps {
    defaultValue?: string
    value?: string
    onChange?: (value: string) => void
    onChangeEnd?: (value: string) => void
    hideLibrary?: boolean
    // hideRecommend?: boolean
    onClickCreateColor?: () => void
    onClickEditColor?: (id: string) => void
}

/**
 * 核心的颜色选择器
 * @description 用于选择颜色、透明度、吸取工具、输入颜色值、选择颜色库中的颜色
 * @note 最近使用的颜色会在组件卸载时（popover 关闭时）自动保存
 */
export const FillPickerCore = ({
    defaultValue,
    value,
    onChange,
    onChangeEnd,
    // hideRecommend,
    hideLibrary,
    onClickCreateColor,
    onClickEditColor
}: FillPickerCoreProps) => {
    const [_value, _onChange] = useUncontrolled({ defaultValue, value, onChange })
    const { palettes, setPendingColor, commitPendingColor } = useFillPickerContext()

    const [hsvaColor, setHsvaColor] = useState<HsvaColor>(inputToHsva(_value, palettes))
    const latestHsva = useLatest(hsvaColor)
    const hexStr = hsvaToHex(hsvaColor)

    // 组件卸载时（popover 关闭时）自动提交待处理的颜色到最近使用列表
    useEffect(() => {
        return () => {
            commitPendingColor()
        }
    }, [commitPendingColor])

    // 处理黑色、白色等无色阶的值
    useEffect(() => {
        if (hexStr === _value) {
            return
        }

        const newHsva = inputToHsva(_value, palettes)
        setHsvaColor({ ...newHsva, h: Number.isNaN(newHsva.h) ? latestHsva.current.h : newHsva.h })
    }, [hexStr, latestHsva, palettes, _value])

    // 追踪颜色变化，设置为待提交（组件卸载时会自动提交）
    const trackColorForRecent = useCallback(
        (colorValue: string) => {
            if (isHexColor(colorValue)) {
                setPendingColor(colorValue)
            }
        },
        [setPendingColor]
    )

    const handleChange = useCallback(
        (value: HsvaColor /* | typeof APP_MAIN_COLOR_VARIABLE */ | ApplicationSettingThemeColor, shouldUpdate?: boolean) => {
            setHsvaColor(() => {
                if (isHsvaColor(value)) {
                    return { ...value, h: Number.isNaN(value.h) ? latestHsva.current.h : value.h }
                }

                const color = chroma(value.value)
                const [h, s, v] = color.hsv()
                const alpha = color.alpha()
                return { h: Number.isNaN(h) ? latestHsva.current.h : h, s, v, a: alpha }
            })

            const newValue = inputToValue(value)
            _onChange(newValue)

            if (shouldUpdate) {
                onChangeEnd?.(newValue)
                trackColorForRecent(newValue)
            }
        },
        [_onChange, latestHsva, onChangeEnd, trackColorForRecent]
    )

    const handleChangeAndEmitUpdate = useCallback(
        (value: HsvaColor /* | typeof APP_MAIN_COLOR_VARIABLE */ | ApplicationSettingThemeColor) => {
            handleChange(value, true)
        },
        [handleChange]
    )

    // 处理 SaturationValuePanel 的 onChangeEnd
    const handleSaturationChangeEnd = useCallback(
        (v: HsvaColor) => {
            const colorValue = inputToValue(v)
            onChangeEnd?.(colorValue)
            trackColorForRecent(colorValue)
        },
        [onChangeEnd, trackColorForRecent]
    )

    // 处理从最近颜色中选择
    const handleSelectRecentColor = useCallback(
        (color: string) => {
            const hsva = hexToHsva(color)
            handleChange(hsva, true)
        },
        [handleChange]
    )

    return (
        <>
            <SaturationValuePanel value={hsvaColor} onChange={handleChange} onChangeEnd={handleSaturationChangeEnd} />

            <ColorPickerContainer value={hsvaColor} onChange={handleChangeAndEmitUpdate} />

            <ColorInputContainer value={hsvaColor} onChange={handleChangeAndEmitUpdate} />

            {/* {!hideRecommend && <ThemeColorContainer onChange={handleChangeAndEmitUpdate} />} */}

            <RecentColors onSelect={handleSelectRecentColor} />

            {!hideLibrary && (
                <ColorLibrary
                    onChange={handleChangeAndEmitUpdate}
                    onClickCreateColor={onClickCreateColor}
                    onClickEditColor={onClickEditColor}
                />
            )}
        </>
    )
}
